import React from 'react'
import { Users, Plus } from 'lucide-react'

import { useTranslation } from '../../hooks/use-i18n'

interface WaitlistPlayer {
  id: string
  name: string
  rating: number
  avatar?: string
}

interface ArenaWaitlistProps {
  players: WaitlistPlayer[]
  isGameOngoing: boolean
  isUserInWaitlist: boolean
  onJoinWaitlist: () => void
  onLeaveWaitlist: () => void
  lightTheme?: boolean
  accentColor?: string
  isAuthenticated?: boolean
  /** Extra classes on the outer card (e.g. branded arena page shadow) */
  surfaceClassName?: string
}

export const ArenaWaitlist: React.FC<ArenaWaitlistProps> = ({
  players,
  isGameOngoing,
  isUserInWaitlist,
  onJoinWaitlist,
  onLeaveWaitlist,
  lightTheme = false,
  accentColor,
  isAuthenticated = true,
  surfaceClassName = '',
}) => {
  const { t } = useTranslation()
  return (
    <div
      className={`rounded-xl p-3 sm:p-4 ${
        lightTheme ? 'border border-gray-200 bg-white text-gray-900 shadow-sm' : 'border border-border bg-card'
      } ${surfaceClassName}`.trim()}
    >
      <div className="flex items-center gap-1.5 sm:gap-2 mb-3 sm:mb-4">
        <Users className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" style={lightTheme && accentColor ? { color: accentColor } : undefined} />
        <h3 className="font-semibold text-sm sm:text-base md:text-lg">{t('chessArena.waitlistForNextGame')}</h3>
      </div>
      {players.length === 0 ? (
        <p className={`text-xs sm:text-sm text-center py-3 sm:py-4 ${lightTheme ? 'text-gray-600' : 'text-muted-foreground'}`}>
          {t('chessArena.noPlayersWaiting')}
        </p>
      ) : (
        <ul className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
          {players.map((player, index) => (
            <li key={player.id} className="flex items-center gap-2">
              <span className={`w-6 text-center text-xs sm:text-sm font-medium flex-shrink-0 ${lightTheme ? 'text-gray-600' : 'text-muted-foreground'}`}>
                #{index + 1}
              </span>
              <span className={`flex-1 truncate text-sm ${lightTheme ? 'text-gray-900' : ''}`}>{player.name}</span>
              <span className={`text-xs sm:text-sm flex-shrink-0 ${lightTheme ? 'text-gray-600' : 'text-muted-foreground'}`}>{player.rating}</span>
            </li>
          ))}
        </ul>
      )}
      {/* Show Join/Leave whenever user can join (authenticated, not playing) - matches chess-arenas behavior */}
      {(isGameOngoing || !isGameOngoing) && (
        <button
          type="button"
          onClick={isUserInWaitlist ? onLeaveWaitlist : onJoinWaitlist}
          disabled={!isAuthenticated && !isUserInWaitlist}
          className={`w-full h-9 sm:h-10 text-xs sm:text-sm rounded-lg font-medium ${
            isUserInWaitlist
              ? 'border border-border hover:bg-muted'
              : 'bg-primary text-primary-foreground hover:opacity-90'
          }`}
          style={!isUserInWaitlist && isAuthenticated && accentColor ? { backgroundColor: accentColor, color: 'white' } : undefined}
        >
          {isUserInWaitlist ? t('chessArena.leaveWaitlist') : !isAuthenticated ? t('chessArena.signInToJoin') : <><Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1" />{t('chessArena.joinWaitlist')}</>}
        </button>
      )}
    </div>
  )
}
